import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';
import {
  LtiPlacementDialogComponent,
  LtiPlacementDialogCourse,
} from '../dialogs/lti-placement-dialog/lti-placement-dialog.component';
import {
  LmsPlacement,
  LmsRegistration,
  LmsRegistryService,
} from '../../services/lms-registry.service';
import { Tutor } from '../../services/tutor.service';

/**
 * One LTI 1.3 registration and its placements — the LTI counterpart of
 * `PlacementsSiteCardComponent`. Deliberately much smaller: LTI has no
 * course list to pull from the LMS, no sync toggles and no outbound
 * push. A row is just (course context → agent, optional course).
 *
 * Rows come from two sources and look identical here:
 *   • an admin created them in this card (manual context_id), or
 *   • al-lti upserted them when a teacher deep-linked the tool in the
 *     LMS (`services/al-lti/app/main.py::_upsert_lti_placement`).
 *
 * Rendered inside the Placements page's "LTI 1.3" tab as a flat
 * `.tile-flat` section (design.md §5 rule 1 — no card-on-card).
 */
@Component({
  selector: 'app-lti-placements-card',
  templateUrl: './lti-placements-card.component.html',
  styleUrl: './lti-placements-card.component.scss',
})
export class LtiPlacementsCardComponent implements OnInit, OnDestroy {
  @Input() registration!: LmsRegistration;
  @Input() tutors: Tutor[] = [];
  @Input() courses: LtiPlacementDialogCourse[] = [];
  /** Needed to recognise the "no course bound" placeholder: placement
   *  rows carry the client_id in `course_id` until a course is chosen. */
  @Input() clientId: string | null = null;

  placements: LmsPlacement[] = [];
  loading = true;

  private destroy$ = new Subject<void>();

  constructor(
    private lms: LmsRegistryService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
    private t: TranslateService,
  ) {}

  ngOnInit(): void {
    this.reload();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reload(): void {
    this.loading = true;
    this.lms
      .listPlacements(this.registration.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          // The endpoint returns every placement of the registration; an
          // LTI registration only ever holds LTI rows, but filter anyway
          // so a stray Moodle-shaped row can't render as an empty context.
          this.placements = rows
            .filter((p) => !!p.lti_context_id)
            .sort((a, b) =>
              String(a.lti_context_id).localeCompare(String(b.lti_context_id)) ||
              String(a.lti_resource_link_id || '').localeCompare(String(b.lti_resource_link_id || '')),
            );
          this.loading = false;
        },
        error: () => {
          this.placements = [];
          this.loading = false;
        },
      });
  }

  trackPlacement = (_: number, p: LmsPlacement) => p.id;

  tutorName(botId: string | null | undefined): string {
    if (!botId) return '';
    const tutor = this.tutors.find((t) => t.id === botId);
    if (!tutor) return botId;
    const visual = (tutor.visual_name || '').trim();
    return visual && visual !== tutor.name ? `${tutor.name} · ${visual}` : tutor.name;
  }

  courseName(courseId: string | null | undefined): string {
    if (!courseId || (this.clientId && courseId === this.clientId)) return '';
    const course = this.courses.find((c) => c.id === courseId);
    return course ? course.course_name : courseId;
  }

  issuerHost(): string {
    const raw = this.registration.lti_issuer || '';
    try {
      return new URL(raw).hostname || raw;
    } catch {
      return raw.replace(/^https?:\/\//, '').replace(/\/+$/, '');
    }
  }

  openCreate(): void {
    this.openDialog(null);
  }

  openEdit(placement: LmsPlacement): void {
    this.openDialog(placement);
  }

  private openDialog(placement: LmsPlacement | null): void {
    this.dialogService
      .open(LtiPlacementDialogComponent, {
        hasScroll: true,
        closeOnBackdropClick: false,
        context: {
          registration: this.registration,
          placement,
          tutors: this.tutors,
          courses: this.courses,
          clientId: this.clientId,
        },
      })
      .onClose.pipe(takeUntil(this.destroy$))
      .subscribe((saved: boolean) => {
        if (saved) this.reload();
      });
  }

  remove(placement: LmsPlacement): void {
    this.dialogService
      .open(ConfirmDialogComponent, {
        hasScroll: true,
        closeOnBackdropClick: true,
        context: {
          title: 'pages.tutors.placements.lti.deleteDialog.title',
          message: 'pages.tutors.placements.lti.deleteDialog.message',
        },
      })
      .onClose.pipe(takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.lms
          .deletePlacement(placement.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.toastr.success(this.t.instant('pages.tutors.placements.lti.deletedToast'), '');
              this.reload();
            },
            error: (err) => {
              this.toastr.danger(err?.error?.detail?.message || err?.error?.detail || this.t.instant('toasts.error'), '');
            },
          });
      });
  }
}
